'use strict';

const moment = require('moment');
const validator = require('validator');

const Globals = require('../globals');
const TypeUtil = require('../shared/TypeUtil');
const Strings = require('./Strings');

const StringUtil = {
    stringifyError: function(err) {
        try {
            return JSON.stringify(err);
        }
        catch(e) {
            return 'Unable to stringify error';
        }
    },

    getStringForRoleToken: function(roleToken, plural) {
        switch (roleToken) {
            case Globals.users.roles.PROJECT_MANAGER:
                if (plural) return 'Project Managers';

                return 'Project Manager';
            case Globals.users.roles.AUTHOR:
                if (plural) return 'Authors';

                return 'Author';
            case Globals.users.roles.ORG_OWNER:
                if (plural) return 'Owners';

                return 'Owner';
            case Globals.users.roles.ORG_ADMIN:
                if (plural) return 'Admins';

                return 'Admin';
            case Globals.users.roles.ORG_SUPERVIEWER:
                if (plural) return 'SuperViewers';

                return 'SuperViewer';
            case Globals.users.roles.ORG_VIEWER:
                if (plural) return 'Viewers';

                return 'Viewer';
            case Globals.users.roles.ORG_PROJECT_CREATOR:
                if (plural) return 'Project Creators';

                return 'Project Creator';
            case Globals.users.roles.ORG_MEMBER:
                if (plural) return 'Members';

                return 'Member';
            case Globals.users.roles.ORG_GUEST:
                if (plural) return 'Guests';

                return 'Guest';
            case Globals.users.roles.CHANNEL_OWNER:
                if (plural) return 'Owners';

                return 'Owner';
            case Globals.users.roles.REVIEWER:
                if (plural) return 'Reviewers';

                return 'Reviewer';
            default:
                return null;
        }
    },

    getTimeStringForMs: function(ms) {
        const timePlayed = moment.duration(ms);
        const hours = Math.floor(timePlayed.asHours()).toString();

        let minutes = (Math.floor(timePlayed.asMinutes()) % 60).toString();
        if (minutes.length === 1) {
            minutes = "0" + minutes;
        }

        let seconds = (Math.floor(timePlayed.asSeconds()) % 60).toString();
        if (seconds.length === 1) {
            seconds = "0" + seconds;
        }

        return hours + ":" + minutes + ":" + seconds;
    },

    getStingForAccountType: function(accountToken) {
        switch(accountToken) {
            case Globals.users.account_types.FREE:
                return Strings['en'].user.account_type.FREE;
            case Globals.users.account_types.FREE_V2:
                return Strings['en'].user.account_type.FREE_V2;
            case Globals.users.account_types.INDIVIDUAL:
                return Strings['en'].user.account_type.INDIVIDUAL;
            case Globals.users.account_types.SCHOOL:
                return Strings['en'].user.account_type.SCHOOL;
            case Globals.users.account_types.ENTERPRISE:
                return Strings['en'].user.account_type.ENTERPRISE;
            default:
                return null;
        }
    },

    getStingForAccountTypeAdmin: function(accountToken) {
        switch(accountToken) {
            case Globals.users.account_types.FREE:
                return Strings['en'].user.account_type_admin.FREE;
            case Globals.users.account_types.FREE_V2:
                return Strings['en'].user.account_type_admin.FREE_V2;
            case Globals.users.account_types.INDIVIDUAL:
                return Strings['en'].user.account_type_admin.INDIVIDUAL;
            case Globals.users.account_types.SCHOOL:
                return Strings['en'].user.account_type_admin.SCHOOL;
            case Globals.users.account_types.ENTERPRISE:
                return Strings['en'].user.account_type_admin.ENTERPRISE;
            default:
                return null;
        }
    },

    formatUSD: function(cents, pad_length) {
        var sign = '';
        var result;
        if (!cents) {
            result = 0.00;
        }
        else {
            sign = cents < 0 ? '-' : '';
            var c = parseInt(cents, 10);
            var absDollars = Math.abs(Math.trunc(c/100));
            var absCents = Math.abs(c % 100);
            if (absCents < 10) {
                absCents = `0${absCents}`;
            }

            result = `${absDollars}.${absCents}`;
        }

        if (pad_length) { // insert some padding
            var spaces = pad_length - result.length;
            for (var i=0; i<spaces; ++i) {
                result = ' ' + result;
            }
        }
        return `${sign}$${result}`;
    },

    getStringForSubscriptionStatus: function(statusToken) {
        switch (statusToken) {
            case Globals.ecommerce.subscription_statuses.trialing:
                return 'Trial';
            case Globals.ecommerce.subscription_statuses.active:
                return 'Active';
            case Globals.ecommerce.subscription_statuses.past_due:
                return 'Past Due';
            case Globals.ecommerce.subscription_statuses.canceled:
                return 'Canceled by user';
            case Globals.ecommerce.subscription_statuses.unpaid:
                return 'Unpaid';
            default:
                // console.warn(`StringUtil.getStringForSubscriptionStatus: String not found for token "${statusToken}"`)
                return '';
        }
    },

    getStringForScoringPolicy: function(scoreTypeToken) {
        switch (scoreTypeToken) {
            case Globals.assignment.score_types.HIGHEST:
                return Strings['en'].score_type.HIGHEST;
            case Globals.assignment.score_types.LATEST:
                return Strings['en'].score_type.LATEST;
            case Globals.assignment.score_types.AVG:
                return Strings['en'].score_type.AVG;
            default:
                console.warn(`StringUtil.getStringForScoringPolicy: String not found for token "${scoreTypeToken}"`)
                return '';
        }
    },

    getStringForNumberOfAttempts: function(numAttempts) {
        if (numAttempts === -1) {
            return Strings['en'].assignment.UNLIMITED;
        }
        else {
            return numAttempts.toString();
        }
    },

    getStringForAttemptStatus: function(statusToken) {
        switch(statusToken) {
            case Globals.attempt.statuses.NEW:
                return 'New';
            case Globals.attempt.statuses.CREATING:
                return 'Creating';
            case Globals.attempt.statuses.READY:
                return 'In Progress';
            case Globals.attempt.statuses.COMPLETE:
                return 'Complete';
            case Globals.attempt.statuses.ERROR:
                return 'Error';
            case Globals.attempt.statuses.ABANDONED:
                return 'Abandoned';
            case Globals.attempt.statuses.ERROR_SENDSCORE:
                return 'Error sending score';
            default:
                console.warn(`StringUtil.getStringForAttemptStatus: String not found for token "${statusToken}"`);
                return '';
        }
    },

    getStringForUserAttemptStatus: function(statusToken) {
        switch (statusToken) {
            case Globals.attempt.user_statuses.NEW:
                return 'Hasn\'t Started';
            case Globals.attempt.user_statuses.STARTED:
                return 'Started';
            case Globals.attempt.user_statuses.IN_PROGRESS:
                return 'In Progress';
            case Globals.attempt.user_statuses.COMPLETE:
                return 'Complete';
            case Globals.attempt.user_statuses.ABANDONED:
                return 'Abandoned';
            case Globals.attempt.user_statuses.ERROR:
                return 'Error';
            case Globals.attempt.user_statuses.ERROR_SENDSCORE:
                return 'Error sending score';
            default:
                // console.warn(`StringUtil.getStringForUserAttemptStatus: String not found for token "${statusToken}"`);
                return '';
        }
    },

    formatRawScore: function(rawScore, format) {
        function tryParseToFixed(scr, digits) {
            let parsedScore = parseInt(scr, 10);
            if (Number.isNaN(scr)) {
                console.error(`formatRawScore - Unable to parse score: ${scr}`);
                return '';
            }

            return parsedScore.toFixed(digits);
        }

        switch(format) {
            case 'percent':
                return `${tryParseToFixed(rawScore, 0)}%`;
            case 'cents':
                return this.formatUSD(rawScore);
            case 'number':
                return tryParseToFixed(rawScore, 0);
            case 'bool':
                if (TypeUtil.parseBoolean(rawScore)) {
                    return 'Yes';
                }
                else {
                    return 'No';
                }

            default:
                return rawScore;
        }
    },

    formatOverallScore: function(scoreNum) {
        // use parseInt to _truncate_ decimal information
        return `${parseInt(scoreNum, 10)}%`;
    },

    formatPercentScore(scoreCategoryObj) {
        if (!!scoreCategoryObj.max_score) {
            // use parseInt to _truncate_ decimal information
            return `${parseInt(Number(scoreCategoryObj.score) * 100 / Number(scoreCategoryObj.max_score), 10)}%`;
        }
        else {
            return `${parseInt(scoreCategoryObj.score, 10)}`;
        }
    },

    // format percentage where input is 0-100+ (rather than 0.0 - 1.0)
    // truncate decimal information
    formatPercentInteger(percentInt) {
        return `${parseInt(percentInt, 10)}%`;
    },

    formatFractionScore(scoreCategoryObj) {
        if (!!scoreCategoryObj.max_score) {
            return `${scoreCategoryObj.score} / ${scoreCategoryObj.max_score}`;
        }
        else {
            return `${parseInt(scoreCategoryObj.score, 10)}`;
        }
    },

    formatBool: function(bool, falseIsBlank) {
        if (!bool) {
            if (falseIsBlank) {
                return '';
            }
            else {
                return 'False';
            }
        }

        return 'True';
    },

    formatBoolYesNo: function(bool, falseIsBlank=false, locale='en') {
        if (!bool) {
            if (falseIsBlank) {
                return '';
            }
            else {
                return Strings[locale].common.NO;
            }
        }

        return Strings[locale].common.YES;
    },

    numberToLocaleString(number, locale='en-US', options) {
        const parsedNumber = parseInt(number, 10);
        if (isNaN(parsedNumber)) {
            console.warn(`numberToLocaleString: Unable to parse "${number}"`);
            return number;
        }

        return parsedNumber.toLocaleString(locale, options);
    },

    getDisplayNameForCommentStatus(statusToken) {
        switch (statusToken) {
            case Globals.activity_comment.status.OPEN: {
                return Strings['en'].activity_comment.status.OPEN;
            }
            case Globals.activity_comment.status.RESOLVED: {
                return Strings['en'].activity_comment.status.RESOLVED;
            }
            default: {
                return Strings['en'].activity_comment.status.UNKNOWN;
            }
        }
    },

    getStringForAssignmentStatus(statusToken) {
        switch (statusToken) {
            case Globals.assignment.statuses.NEW: {
                return Strings['en'].assignment.status.NEW;
            }
            case Globals.assignment.statuses.SETTING_UP: {
                return Strings['en'].assignment.status.SETTING_UP;
            }
            case Globals.assignment.statuses.CREATING: {
                return Strings['en'].assignment.status.CREATING;
            }
            case Globals.assignment.statuses.PENDING_READY: {
                return Strings['en'].assignment.status.PENDING_READY;
            }
            case Globals.assignment.statuses.READY: {
                return Strings['en'].assignment.status.READY;
            }
            case Globals.assignment.statuses.INTERVENTION_NEEDED: {
                return Strings['en'].assignment.status.INTERVENTION_NEEDED;
            }
            case Globals.assignment.statuses.COMPLETE: {
                return Strings['en'].assignment.status.COMPLETE;
            }
            case Globals.assignment.statuses.ERROR: {
                return Strings['en'].assignment.status.ERROR;
            }
            default: {
                return Strings['en'].common.UNKNOWN;
            }
        }
    },
    
    getStringForPlayCloseDialog(state) {
        var txt = 'Are you sure you want to close the activity?';
        if (state.playComplete === true) {
            // Note: this case doesn't currently need text
        }
        else {
            if (state.resumeSupported === true) {
                // if (state.unsavedProgress) <- doesn't change message
                txt += ' Your progress will be saved and you will resume where you left off when you return.';
            }
            else if (state.resumeSupported === false) {
                txt += ' Your progress will not be saved and you will start over at the beginning when you return.';
            }
            else { // if (typeof state.resumeSupported === 'undefined') {
                // Note: this case doesn't currently need text  
            }
        }
        return txt;
    },

    getStringForTurnType(turnTypeToken) {
        switch (turnTypeToken) {
            case Globals.assignment.turn_type.MANUAL: {
                return Strings['en'].assignment.turn_type.MANUAL;
            }
            case Globals.assignment.turn_type.INSTRUCTOR_CONTROLLED: {
                return Strings['en'].assignment.turn_type.CONTROLLED;
            }
            case Globals.assignment.turn_type.SCHEDULED: {
                return Strings['en'].assignment.turn_type.SCHEDULED;
            }
            default: {
                return '';
            }
        }
    },

    getStringForScoreRange(token, locale="en") {
        return Strings[locale].analytics.score_ranges[token];
    },

    getStringForTimePlayedRange(token, locale="en") {
        return Strings[locale].analytics.time_played_ranges[token];
    },

    getStringAbbrevForQuestionType(token, questionQuality, locale="en") {
        let abbrev = Strings[locale].analytics.question_type.abbrev[token];
        // override for FIBQ Assess vs. Store
        if (token === Globals.analytics.question_type.FIBQ) {
            if (questionQuality === Globals.analytics.question_quality.FACT) {
                abbrev = Strings[locale].analytics.question_type.abbrev.sub_type.FIBQA;
            }
            else if (questionQuality === Globals.analytics.question_quality.OPINION) {
                abbrev = Strings[locale].analytics.question_type.abbrev.sub_type.FIBQS;
            }

        }

        if (!abbrev) {
            return Strings[locale].common.UNKNOWN;
        }

        return abbrev;
    },

    getStringForQuestionQuality(token, locale="en") {
        if (token === Globals.analytics.question_quality.FACT) {
            return Strings[locale].analytics.question_quality.FACT;
        }
        else if (token === Globals.analytics.question_quality.OPINION) {
            return Strings[locale].analytics.question_quality.OPINION;
        }
        else {
            return Strings[locale].common.UNKNOWN;
        }

    },

    unescapeString(str) {
        if (typeof str !== 'string') {
            return str;
        }

        return validator.unescape(str);
    },

    dangerouslyCreateMarkup(str) {
        return { __html: str };
    },
};

module.exports = StringUtil;